// AdminAppShopOrdersPage — Phase 6 · shop orders passing through app.geovinum.io.
import { useCallback, useEffect, useState } from "react";
import { ShoppingBag, RefreshCw, Loader2, AlertCircle, ExternalLink, Search } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const STATUS_LABELS = {
  paid: { label: "Payée", color: "bg-blue-50 text-blue-700" },
  in_production: { label: "En production", color: "bg-amber-50 text-amber-700" },
  shipped: { label: "Expédiée", color: "bg-emerald-50 text-emerald-700" },
  delivered: { label: "Livrée", color: "bg-emerald-100 text-emerald-800" },
  failed: { label: "Échec", color: "bg-red-50 text-red-700" },
};

const AdminAppShopOrdersPage = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listAppShopOrders({ status, limit: 200 });
      const list = Array.isArray(res) ? res : res?.items || res?.orders || [];
      setItems(list);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = query.trim()
    ? items.filter((o) =>
        (o.customer_email || o.email || "").toLowerCase().includes(query.toLowerCase()) ||
        (o.order_id || o.id || "").toLowerCase().includes(query.toLowerCase()) ||
        (o.product || o.product_title || "").toLowerCase().includes(query.toLowerCase())
      )
    : items;

  const totalEur = filtered.reduce((s, o) => s + (o.amount_eur ?? (o.amount_cents || 0) / 100), 0);

  return (
    <div data-testid="admin-app-shop-orders">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Commandes app.geovinum.io</h1>
        <button onClick={fetchAll} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px]" data-testid="shop-refresh-btn">
          <RefreshCw size={13} />
        </button>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">Commandes Gelato passées via app.geovinum.io — tracking, statuts et sommes.</p>

      <div className="flex gap-2 mb-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Email / commande / produit…" className="gv-input w-full pl-8" data-testid="shop-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="gv-input" data-testid="shop-status-filter">
          <option value="">Tous statuts</option>
          <option value="paid">Payée</option>
          <option value="in_production">En production</option>
          <option value="shipped">Expédiée</option>
          <option value="delivered">Livrée</option>
          <option value="failed">Échec</option>
        </select>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="shop-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Impossible de récupérer les commandes</div>
            <div className="text-[12px] opacity-70 mt-1">Vérifiez que l'app.geovinum.io backend est en ligne.</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="shop-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : error ? null : filtered.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="shop-empty">
            <ShoppingBag size={14} /> {query ? "Aucun résultat" : "Aucune commande"}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Commande</th>
                <th className="text-left px-4 py-2.5 font-medium">Client</th>
                <th className="text-left px-4 py-2.5 font-medium">Produit</th>
                <th className="text-left px-4 py-2.5 font-medium">Qté</th>
                <th className="text-left px-4 py-2.5 font-medium">Montant</th>
                <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                <th className="text-left px-4 py-2.5 font-medium">Tracking</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, idx) => {
                const id = o.order_id || o.id || `order-${idx}`;
                const sk = STATUS_LABELS[o.status] || { label: o.status || "—", color: "bg-gray-50 text-gray-600" };
                const amt = o.amount_eur ?? (o.amount_cents || 0) / 100;
                return (
                  <tr key={id} className="border-t border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/30" data-testid={`shop-order-row-${id}`}>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)] font-mono text-[11.5px]">{id}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/80">{o.customer_email || o.email || "—"}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/80">{o.product || o.product_title || "—"}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/70">{o.quantity ?? 1}</td>
                    <td className="px-4 py-2 text-[color:var(--gv-ink)]/85">{amt.toFixed(2)} €</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${sk.color}`}>{sk.label}</span>
                    </td>
                    <td className="px-4 py-2">
                      {o.tracking_url ? (
                        <a href={o.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[12px] text-[color:var(--gv-pink)] hover:underline">
                          Suivre <ExternalLink size={11} />
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex justify-between text-[11.5px] text-[color:var(--gv-ink)]/55 mt-2">
        <span>{filtered.length} commande{filtered.length > 1 ? "s" : ""}</span>
        <span>Total : <strong className="text-[color:var(--gv-ink)]">{totalEur.toFixed(2)} €</strong></span>
      </div>
    </div>
  );
};

export default AdminAppShopOrdersPage;
